import {Checkbox} from "@octopusdeploy/design-system-components";
import {DataBaseComponent, DataBaseComponentState} from "components/DataBaseComponent";
import SaveDialogLayout from "components/DialogLayout/SaveDialogLayout";
import repository from "client/repository";
import * as React from "react";
import ReefMultiSelect from "shared/ReefMultiSelect";
import {ReefResource} from "client/resources/reefResource";
import InternalRedirect from "components/Navigation/InternalRedirect";
import {backgroundTasksRouteLinks} from "areas/backgroundTasks/backgroundTasksRouteLinks";
import { Callout } from "@octopusdeploy/design-system-components";
import {Text} from "~/components/form";
import {Number} from "~/shared/Number";
import {
    HostedInstanceBulkRotateRedisCachePasswordRequest
} from "~/client/resources/HostedInstanceBulkRotateRedisCachePasswordRequest";

// eslint-disable-next-line @typescript-eslint/no-empty-interface
interface Props {
}

interface State extends DataBaseComponentState {
    hostedInstanceIdsDelimitedByLine: string;
    reefs: Map<string, ReefResource>;
    rotationRequest: HostedInstanceBulkRotateRedisCachePasswordRequest;
    redirectTo?: string;
}

class BulkRotateRedisCachePasswordDialog extends DataBaseComponent<Props, State> {
    constructor(props: Props) {
        super(props);

        this.state = {
            hostedInstanceIdsDelimitedByLine: "",
            reefs: new Map<string, ReefResource>(),
            rotationRequest: {
                HostedInstanceIds: [],
                ReefIds: [],
                RotateDuringWeekends: false,
                MaxConcurrentRotationsPerReef: 30
            },
        };
    }

    componentDidMount() {
        return this.doBusyTask(async () => {
            const reefs = repository.Reefs.map();

            this.setState({
                reefs: await reefs,
            });
        });
    }

    render() {
        if (this.state.redirectTo) {
            return <InternalRedirect to={this.state.redirectTo}/>;
        }

        return <SaveDialogLayout title={`Bulk Rotate Redis Cache Passwords`}
                                 onSaveClick={() => this.bulkRotateRedisCachePasswords()}
                                 errors={this.state.errors}
                                 busy={this.state.busy}>

            <Callout type="information" title="Further Action">
                Instances excluded from the <strong>Participate in bulk Instance Redis cache password rotation</strong> Portal
                Feature Flag will not have their Redis cache passwords rotated. These Instances require manual rotation.
            </Callout>

            <Checkbox value={this.state.rotationRequest.RotateDuringWeekends}
                      onChange={v => this.updateRotationRequest({RotateDuringWeekends: v})}
                      label="Rotate keys during weekends"/>
            <p>
                If unticked, Redis cache passwords will be rotated only on weekdays. If ticked, passwords will also rotate on
                weekends.
            </p>

            <ReefMultiSelect
                items={this.state.reefs ? Array.from(this.state.reefs.values()) : []}
                value={this.state.rotationRequest.ReefIds}
                onChange={ReefIds => this.updateRotationRequest({ReefIds})}
            />
            <p>
                All Instances will have their Redis cache passwords rotated only if they are on the selected Reefs.
                If no Reef has been selected, every Instance's passwords will rotate.
            </p>

            <Text label="Hosted Instance IDs (one per line)"
                  value={this.state.hostedInstanceIdsDelimitedByLine}
                  onChange={ids => this.updateHostedInstanceIds(ids)}
                  multiLine={true}
                  hintText={"IDs, e.g. HostedInstances-1"}
            />

            <Number label="Max Concurrent Rotations per Reef"
                    value={this.state.rotationRequest.MaxConcurrentRotationsPerReef}
                    onChange={v => this.updateRotationRequest({MaxConcurrentRotationsPerReef: v})}
            />
            <p>
                The number of Instances allowed to rotate in parallel. Lower values are less risky, but will take longer to complete.
            </p>
        </SaveDialogLayout>;
    }

    private updateRotationRequest(update: Partial<HostedInstanceBulkRotateRedisCachePasswordRequest>) {
        return this.setState(prevState => ({rotationRequest: {...prevState.rotationRequest, ...update}}));
    }

    private updateHostedInstanceIds(idsDelimitedByLine: string) {
        return this.setState(prevState => ({
            hostedInstanceIdsDelimitedByLine: idsDelimitedByLine,
            rotationRequest: {
                ...prevState.rotationRequest,
                HostedInstanceIds: idsDelimitedByLine.split("\n").filter(id => id.trim() !== "")
            },
        }));
    }

    private async bulkRotateRedisCachePasswords(): Promise<boolean> {
        return await this.doBusyTask(async () => {
            const backgroundTask = await repository.HostedInstances.bulkRotateRedisCachePasswords(this.state.rotationRequest);
            this.setState({redirectTo: backgroundTasksRouteLinks.task(backgroundTask.Id).root})
        });
    }
}

export default BulkRotateRedisCachePasswordDialog;
